/**
 * 지출결의등록2 — 사업 기준 전자증빙→과목 매핑 → 과목별 결의 일괄 생성
 * 기존 ExpenseResolutionWorkspace(축 먼저)와 병렬 PoC. 저장 SSOT는 동일 expenseStore.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { PageContainer, CommonCss } from 'styles/common.styles';
import { theme, SEARCH_LABEL_W } from 'styles/theme';
import {
  FormArea,
  CollapsibleSearchPanel,
  CollapsibleSectionPanel,
  ActionButtonGroup,
  getCurrentFiscalYear,
} from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  GridExportProvider,
  GridExportFocusScope,
  useRegisterGridExport,
  useExcelExportAction,
} from 'components/grid/excel';
import {
  ActiveGridProvider,
  ActiveGridScope,
  ActiveGridHeader,
  useActiveGridCrudActions,
} from 'contexts/ActiveGridContext';
import {
  GridPanel,
  GridBody,
  Meta,
} from 'components/budget/BudgetFormationChrome.styles';
import { showAlert, showConfirm } from 'utils/toastBridge';
import CodeLookupDialog from 'components/budget/CodeLookupDialog';
import {
  EVIDENCE_SOURCE,
  ensureEvidenceSeed,
  filterOpenEvidences,
  loadEvidences,
  fetchEvidences,
  syncEvidencesForResolution,
} from 'expense/expenseEvidenceStore';
import {
  EXPENSE_STATUS,
  expenseStatusLabel,
  loadResolutionBundle,
  saveResolutionBundle,
  loadObligationBundle,
  saveObligationBundle,
  ensureObligationForResolution,
} from 'expense/expenseStore';
import {
  listProjectsForResolutionByProject,
  listSubjectsForProject,
  subjectComboOptions,
  resolveProjectAxisHint,
  buildResolutionsBySubjectGroups,
  inferSingleProjectFromEvidences,
} from 'expense/resolutionByProjectUtils';

const EVIDENCE_GRID_ID = 'expense-res2-evidence';
const SUBJECT_GRID_ID = 'expense-res2-subject';
const RESULT_GRID_ID = 'expense-res2-result';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
  height: 100%;
  overflow: auto;
`;

const Hint = styled.div`
  font-size: 0.75rem;
  color: ${theme.colors.text?.secondary || '#6b7280'};
  line-height: 1.45;
  padding: 0 2px 4px;
`;

function ExpenseResolutionByProjectInner() {
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = buildPrefsUserId(user, tenantCode);

  const methods = useForm({
    defaultValues: {
      fiscalYear: getCurrentFiscalYear(),
      projectCode: '',
      projectName: '',
      projectDisp: '',
    },
  });
  const { control, getValues, setValue } = methods;
  const projectCode = useWatch({ control, name: 'projectCode' });

  const [axisHint, setAxisHint] = useState({});
  const [subjectRows, setSubjectRows] = useState([]);
  const [resultRows, setResultRows] = useState([]);
  const [projectLookupOpen, setProjectLookupOpen] = useState(false);
  const [loadedAt, setLoadedAt] = useState('');

  const evidenceRef = useRef(null);
  const subjectRef = useRef(null);
  const resultRef = useRef(null);

  const evidenceGrid = useHiGridHook({ enableContextMenu: true });
  const subjectGrid = useHiGridHook({ enableContextMenu: false });

  const subjectOptions = useMemo(() => subjectComboOptions(subjectRows), [subjectRows]);

  const dims = useCallback(
    () => ({
      tenantCode,
      fiscalYear: String(getValues('fiscalYear') || getCurrentFiscalYear()).trim(),
    }),
    [tenantCode, getValues]
  );

  const refreshSubjects = useCallback(
    (projCode, evidences, hint) => {
      const d = dims();
      const axis =
        hint ||
        resolveProjectAxisHint(tenantCode, d.fiscalYear, projCode, evidences || []);
      setAxisHint(axis);
      const subjects = listSubjectsForProject(tenantCode, d.fiscalYear, projCode, axis);
      setSubjectRows(subjects);
      subjectRef.current?.setData?.(subjects);
      return { axis, subjects };
    },
    [dims, tenantCode]
  );

  /** F7 — 미결 법인카드 증빙 불러오기 */
  const handleLoadEvidences = useCallback(async () => {
    const d = dims();
    if (!d.fiscalYear) {
      showAlert('회계연도를 입력하세요.');
      return;
    }
    ensureEvidenceSeed(tenantCode, d.fiscalYear);
    await fetchEvidences(tenantCode, d.fiscalYear);
    const open = filterOpenEvidences(loadEvidences(tenantCode, d.fiscalYear), {
      source: EVIDENCE_SOURCE.CARD,
    }).map((e) => ({
      ...e,
      id: e.evidenceUid,
      mapSubjectCode: '',
      mapSubjectName: '',
    }));
    evidenceRef.current?.setData?.(open);
    setLoadedAt(new Date().toLocaleTimeString());
    setResultRows([]);
    resultRef.current?.setData?.([]);

    const inferred = inferSingleProjectFromEvidences(tenantCode, open);
    if (inferred?.projectCode) {
      setValue('projectCode', inferred.projectCode);
      setValue('projectName', inferred.projectName || '');
      setValue(
        'projectDisp',
        inferred.projectName
          ? `${inferred.projectCode} · ${inferred.projectName}`
          : inferred.projectCode
      );
      refreshSubjects(inferred.projectCode, open);
      showAlert(
        `미결 카드 ${open.length}건 불러옴. 사업 ${inferred.projectCode} 자동 선택.`,
        'success'
      );
    } else {
      setValue('projectCode', '');
      setValue('projectName', '');
      setValue('projectDisp', '');
      setSubjectRows([]);
      subjectRef.current?.setData?.([]);
      showAlert(
        open.length
          ? `미결 카드 ${open.length}건. 사업을 선택하세요.`
          : '미결 법인카드 증빙이 없습니다.',
        open.length ? 'success' : 'warning'
      );
    }
  }, [dims, tenantCode, setValue, refreshSubjects]);

  useEffect(() => {
    handleLoadEvidences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const code = String(projectCode || '').trim();
    if (!code) return;
    const ev = evidenceRef.current?.getData?.() || [];
    refreshSubjects(code, ev);
  }, [projectCode, refreshSubjects]);

  useEffect(() => {
    evidenceGrid.onAfterSaveCell = (rowData, { field }) => {
      if (field !== 'mapSubjectCode' || !rowData?.evidenceUid) return;
      const sub = subjectRows.find(
        (s) => String(s.subjectCode) === String(rowData.mapSubjectCode || '').trim()
      );
      const patch = { mapSubjectName: sub?.subjectName || '' };
      const data = evidenceRef.current?.getData?.() || [];
      evidenceRef.current?.setData?.(
        data.map((r) =>
          r.evidenceUid === rowData.evidenceUid ? { ...r, ...rowData, ...patch } : r
        )
      );
    };
  }, [evidenceGrid, subjectRows]);

  const openProjectLookup = useCallback(() => {
    setProjectLookupOpen(true);
  }, []);

  const projectLookupConfig = useMemo(() => {
    const d = dims();
    const ev = evidenceRef.current?.getData?.() || [];
    const rows = listProjectsForResolutionByProject(tenantCode, d.fiscalYear, ev);
    return {
      title: '사업 선택 (카드축·현액)',
      rows,
      columns: [
        hiCol.roTextColumn({ header: '사업코드', field: 'projectCode', size: 110 }),
        hiCol.roTextColumn({ header: '사업명', field: 'projectName', size: 200 }),
        hiCol.roTextColumn({ header: '부서', field: 'deptName', size: 120 }),
        hiCol.roTextColumn({ header: '출처', field: 'source', size: 90 }),
      ],
      enableSearch: true,
      searchConfig: {
        mode: 'unified',
        keywordLabel: '검색',
        keywordFields: ['projectCode', 'projectName', 'deptName', 'code', 'name'],
      },
    };
  }, [dims, tenantCode, projectLookupOpen, loadedAt]);

  const applyProject = useCallback(
    (row) => {
      if (!row) return;
      const code = String(row.projectCode || '').trim();
      const name = String(row.projectName || '').trim();
      setValue('projectCode', code);
      setValue('projectName', name);
      setValue('projectDisp', name ? `${code} · ${name}` : code);
      const ev = evidenceRef.current?.getData?.() || [];
      refreshSubjects(code, ev, row);
      setProjectLookupOpen(false);
    },
    [setValue, refreshSubjects]
  );

  const evidenceColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '사용일', field: 'useDate', size: 100, align: 'center' }),
      hiCol.roTextColumn({ header: '카드', field: 'cardNo', size: 130 }),
      hiCol.roTextColumn({ header: '가맹점', field: 'merchantName', size: 140 }),
      hiCol.roNumberColumn({ header: '금액', field: 'amount', size: 100, align: 'right' }),
      hiCol.roNumberColumn({ header: '부가세', field: 'vatAmt', size: 90, align: 'right' }),
      hiCol.roTextColumn({ header: '비고', field: 'remark', size: 120 }),
      hiCol.edComboColumn({
        header: '매핑과목',
        field: 'mapSubjectCode',
        size: 200,
        required: true,
        options: subjectOptions,
      }),
      hiCol.roTextColumn({ header: '과목명', field: 'mapSubjectName', size: 140 }),
    ],
    [subjectOptions]
  );

  const subjectColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '과목코드', field: 'subjectCode', size: 110 }),
      hiCol.roTextColumn({ header: '과목명', field: 'subjectName', size: 160 }),
      hiCol.roTextColumn({ header: '부서', field: 'deptName', size: 100 }),
      hiCol.roTextColumn({ header: '재원', field: 'fundName', size: 90 }),
      hiCol.roNumberColumn({ header: '현액', field: 'balanceAmt', size: 100, align: 'right' }),
      hiCol.roNumberColumn({ header: '가용', field: 'availableAmt', size: 100, align: 'right' }),
    ],
    []
  );

  const resultColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '결의번호', field: 'resolutionNo', size: 110, align: 'center' }),
      hiCol.roTextColumn({ header: '과목코드', field: 'subjectCode', size: 100 }),
      hiCol.roTextColumn({ header: '과목명', field: 'subjectName', size: 140 }),
      hiCol.roNumberColumn({ header: '증빙건수', field: 'count', size: 80, align: 'right' }),
      hiCol.roNumberColumn({ header: '결의금액', field: 'amount', size: 110, align: 'right' }),
      hiCol.roTextColumn({ header: '상태', field: 'statusNm', size: 80, align: 'center' }),
    ],
    []
  );

  /** 과목 더블클릭 → 포커스 증빙(없으면 미매핑 전체)에 배정 */
  useEffect(() => {
    subjectGrid.onGridCellDoubleClicked = async (data) => {
      const sc = String(data?.subjectCode || '').trim();
      if (!sc) return;
      const sn = String(data?.subjectName || '').trim();
      const focused = evidenceRef.current?.getSelectedRow?.();
      const all = evidenceRef.current?.getData?.() || [];
      let targets = focused ? [focused] : [];
      if (!targets.length) {
        const unmapped = all.filter((r) => !String(r.mapSubjectCode || '').trim());
        if (!unmapped.length) {
          showAlert('매핑할 증빙을 선택하거나, 미매핑 증빙이 있어야 합니다.', 'warning');
          return;
        }
        const ok = await showConfirm(
          `선택 행이 없어 미매핑 ${unmapped.length}건에 「${sc} ${sn}」을 배정할까요?`
        );
        if (!ok) return;
        targets = unmapped;
      }
      const uidSet = new Set(targets.map((r) => r.evidenceUid));
      evidenceRef.current?.setData?.(
        all.map((r) =>
          uidSet.has(r.evidenceUid) ? { ...r, mapSubjectCode: sc, mapSubjectName: sn } : r
        )
      );
      showAlert(`${targets.length}건 → ${sc} ${sn}`, 'success');
    };
  }, [subjectGrid]);

  const handleResetMapping = useCallback(() => {
    const all = evidenceRef.current?.getData?.() || [];
    evidenceRef.current?.setData?.(
      all.map((r) => ({ ...r, mapSubjectCode: '', mapSubjectName: '' }))
    );
    showAlert('과목 매핑을 초기화했습니다.', 'success');
  }, []);

  /** F10 — 과목별 결의 일괄 생성·저장 */
  const handleCreateResolutions = useCallback(async () => {
    const d = dims();
    const projCode = String(getValues('projectCode') || '').trim();
    const projName = String(getValues('projectName') || '').trim();
    if (!projCode) {
      showAlert('사업을 선택하세요.');
      return;
    }
    const mapped = (evidenceRef.current?.getData?.() || []).filter((r) => r.mod !== 'D');
    if (!mapped.length) {
      showAlert('매핑할 증빙이 없습니다. F7로 미결 증빙을 불러오세요.');
      return;
    }
    const unmapped = mapped.filter((r) => !String(r.mapSubjectCode || '').trim());
    if (unmapped.length) {
      showAlert(`과목이 배정되지 않은 증빙이 ${unmapped.length}건 있습니다.`);
      return;
    }

    const { subjects } = refreshSubjects(projCode, mapped);
    const hint = resolveProjectAxisHint(tenantCode, d.fiscalYear, projCode, mapped);
    const built = buildResolutionsBySubjectGroups({
      fiscalYear: d.fiscalYear,
      projectCode: projCode,
      projectName: projName,
      subjectRows: subjects,
      axisHint: hint,
      mappedRows: mapped,
    });

    if (!built.masters.length) {
      showAlert('생성할 결의가 없습니다.');
      return;
    }

    const lines = built.summary
      .map(
        (s) =>
          `· ${s.subjectCode} ${s.subjectName}: ${s.count}건 / ${s.amount.toLocaleString()}원`
      )
      .join('\n');
    const ok = await showConfirm(
      `사업 ${projCode} 기준으로 과목별 결의 ${built.masters.length}건을 생성할까요?\n\n${lines}`
    );
    if (!ok) return;

    let resBundle = await loadResolutionBundle(d);
    let oblBundle = await loadObligationBundle(d);
    const created = [];

    for (const master0 of built.masters) {
      const groupDetails = built.details.filter((x) => x.resolutionUid === master0.resolutionUid);
      const ensured = ensureObligationForResolution(d, master0, oblBundle, resBundle);
      const master = ensured.resolutionMaster;
      oblBundle = ensured.obligationBundle || oblBundle;

      resBundle = {
        masters: [...(resBundle.masters || []), { ...master, mod: 'A' }],
        details: [...(resBundle.details || []), ...groupDetails],
      };

      const saved = await saveResolutionBundle(d, resBundle.masters, resBundle.details);
      resBundle = saved;

      const savedMaster = saved.masters.find((m) => m.resolutionUid === master.resolutionUid);
      const evUids = groupDetails.map((x) => x.evidenceUid).filter(Boolean);
      syncEvidencesForResolution(tenantCode, d.fiscalYear, {
        resolutionUid: master.resolutionUid,
        resolutionNo: savedMaster?.resolutionNo || master.resolutionNo || '',
        evidenceUids: evUids,
      });

      created.push({
        ...savedMaster,
        statusNm: expenseStatusLabel(savedMaster?.status || EXPENSE_STATUS.DRAFT),
        count: groupDetails.length,
        amount: Number(savedMaster?.resolutionAmt) || 0,
        id: savedMaster?.resolutionUid,
      });
    }

    if (oblBundle) {
      await saveObligationBundle(d, oblBundle.masters, oblBundle.vendors, oblBundle.contracts);
    }

    setResultRows(created);
    resultRef.current?.setData?.(created);
    handleLoadEvidences();
    showAlert(
      `과목별 결의 ${created.length}건을 저장했습니다. (작성중)\n기존「지출결의등록」·승인 화면에서 이어서 처리할 수 있습니다.`,
      'success'
    );
  }, [dims, getValues, refreshSubjects, tenantCode, handleLoadEvidences]);

  const { handleActiveAdd, handleActiveDelete, activeLabel } = useActiveGridCrudActions({
    guardAdd: () => '추가 대신 과목 더블클릭 또는 매핑과목 콤보를 사용하세요.',
    guardDelete: (id) =>
      id === EVIDENCE_GRID_ID ? null : '매핑 초기화는 증빙 그리드가 활성일 때 F9로 하세요.',
  });

  // F9 on evidence grid → reset mapping (via ActiveGridScope onDelete)
  const onEvidenceDelete = handleResetMapping;

  const searchFields = useMemo(
    () => [
      [
        {
          name: 'fiscalYear',
          label: '회계연도',
          type: 'fiscalYear',
          width: CommonCss.w80,
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'projectDisp',
          label: '사업',
          type: 'text',
          width: CommonCss.w240,
          labelWidth: SEARCH_LABEL_W,
          readonly: true,
          showSearch: true,
          searchTitle: '사업 선택',
          onSearch: openProjectLookup,
          placeholder: '카드축·현액 사업',
          title: '증빙에 매핑된 카드의 기본 사업 또는 현액 사업',
        },
      ],
    ],
    [openProjectLookup]
  );

  useRegisterGridExport({
    id: EVIDENCE_GRID_ID,
    getData: () => evidenceRef.current?.getData?.() || [],
    getDataColumns: () => evidenceRef.current?.getDataColumns?.() || [],
    label: '증빙매핑',
  });

  const { openDialog: openExcelDialog, dialog: excelDialog } = useExcelExportAction({
    fileName: '지출결의등록2',
  });

  const actionButtons = useMemo(
    () => [
      { id: 'search', label: '조회', icon: '🔍', onClick: handleLoadEvidences, hotkey: 'F7', title: '미결 증빙 불러오기 (F7)' },
      { id: 'add', label: '추가', icon: '➕', onClick: handleActiveAdd, hotkey: 'F8' },
      {
        id: 'delete',
        label: '매핑초기화',
        icon: '❌',
        onClick: handleActiveDelete,
        hotkey: 'F9',
        variant: 'danger',
        title: activeLabel ? `매핑초기화 → ${activeLabel}` : '매핑 초기화 (F9)',
      },
      {
        id: 'save',
        label: '결의생성',
        icon: '✅',
        onClick: handleCreateResolutions,
        hotkey: 'F10',
        variant: 'success',
        title: '과목별 결의 생성·저장 (F10)',
      },
      {
        id: 'excel',
        label: '엑셀',
        icon: '📥',
        onClick: openExcelDialog,
        hotkey: 'F11',
      },
    ],
    [
      handleLoadEvidences,
      handleActiveAdd,
      handleActiveDelete,
      handleCreateResolutions,
      openExcelDialog,
      activeLabel,
    ]
  );

  const mapHint = subjectRows.some((s) => s._pocFallback)
    ? '현액에 해당 사업 과목이 없어 PoC 폴백 과목(수용비·사무용품비·회의비·여비)을 표시합니다.'
    : '선택 사업의 현액(지출) 과목입니다. 행 더블클릭 시 포커스 증빙(또는 미매핑 전체)에 과목을 배정합니다.';

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} />
        <Stack>
          <CollapsibleSearchPanel
            storageKey="expense-res2-search"
            prefsUserId={prefsUserId}
            title="조회·사업"
          >
            <FormArea fields={searchFields} compact />
          </CollapsibleSearchPanel>

          <Hint>
            <strong>업무 흐름:</strong> ① 미결 카드증빙 불러오기(F7) → ② 사업 선택 → ③ 증빙마다
            매핑과목 지정(콤보 또는 과목 더블클릭) → ④ F10으로 <em>과목별 결의</em> 일괄 생성. 같은
            사업·다른 과목(수용비·사무용품비·회의비 등)을 한 번에 처리합니다. 기존
            「지출결의등록」은 유지됩니다.
            {loadedAt ? ` · 최근 로드 ${loadedAt}` : ''}
            {activeLabel ? ` · 활성: ${activeLabel}` : ''}
            {axisHint?.deptName ? ` · 부서 ${axisHint.deptName}` : ''}
          </Hint>

          <ActiveGridScope gridId={EVIDENCE_GRID_ID} onDelete={onEvidenceDelete}>
            <GridPanel style={{ minHeight: 280, flex: '1 1 280px' }}>
              <ActiveGridHeader gridId={EVIDENCE_GRID_ID}>
                미결 전자증빙 → 과목 매핑
              </ActiveGridHeader>
              <GridBody>
                <GridExportFocusScope gridId={EVIDENCE_GRID_ID}>
                  <HiGridComponent
                    ref={evidenceRef}
                    columns={evidenceColumns}
                    height="100%"
                    showRowNumber
                    rowNumberSize={56}
                    enableColumnFilters
                    enableContextMenu
                    enableRowEditMenu
                    enableCut
                    enableFindReplace
                    allowReplace
                    enableHeaderMenu
                    enableSelectionStats
                    enableFreeze
                    enableQuickExport
                    exportFileName="결의2-증빙매핑"
                    gridId={EVIDENCE_GRID_ID}
                    prefsUserId={prefsUserId}
                    {...evidenceGrid.contextMenuProps}
                  />
                </GridExportFocusScope>
              </GridBody>
            </GridPanel>
          </ActiveGridScope>

          <ActiveGridScope gridId={SUBJECT_GRID_ID}>
            <GridPanel style={{ minHeight: 180, flex: '0 0 200px' }}>
              <ActiveGridHeader gridId={SUBJECT_GRID_ID}>
                {projectCode
                  ? `사업 ${projectCode} 편성 과목`
                  : '사업 편성 과목 (사업 선택 후 표시)'}
              </ActiveGridHeader>
              <Meta>{mapHint}</Meta>
              <GridBody>
                <HiGridComponent
                  ref={subjectRef}
                  columns={subjectColumns}
                  height="100%"
                  showRowNumber
                  rowNumberSize={56}
                  enableColumnFilters
                  enableHeaderMenu
                  enableSelectionStats
                  enableFreeze
                  enableQuickExport
                  exportFileName="결의2-과목"
                  gridId={SUBJECT_GRID_ID}
                  prefsUserId={prefsUserId}
                  {...subjectGrid.contextMenuProps}
                />
              </GridBody>
            </GridPanel>
          </ActiveGridScope>

          <CollapsibleSectionPanel
            storageKey="expense-res2-result"
            prefsUserId={prefsUserId}
            title="이번 생성 결의"
            summary={resultRows.length ? `${resultRows.length}건` : ''}
            defaultCollapsed={false}
            expandedHeight="180px"
          >
            <ActiveGridScope gridId={RESULT_GRID_ID}>
              <HiGridComponent
                ref={resultRef}
                columns={resultColumns}
                height="100%"
                showRowNumber
                rowNumberSize={56}
                enableColumnFilters
                enableHeaderMenu
                enableQuickExport
                exportFileName="결의2-생성결과"
                gridId={RESULT_GRID_ID}
                prefsUserId={prefsUserId}
              />
            </ActiveGridScope>
          </CollapsibleSectionPanel>
        </Stack>
      </FormProvider>

      <CodeLookupDialog
        open={projectLookupOpen}
        title={projectLookupConfig.title}
        columns={projectLookupConfig.columns}
        rows={projectLookupConfig.rows}
        enableSearch={projectLookupConfig.enableSearch}
        searchConfig={projectLookupConfig.searchConfig}
        multiSelect={false}
        prefsUserId={prefsUserId}
        sizeKey="codeLookup-res2-project"
        onClose={() => setProjectLookupOpen(false)}
        onSelect={applyProject}
      />
      {excelDialog}
    </PageContainer>
  );
}

export default function ExpenseResolutionByProjectWorkspace() {
  return (
    <GridExportProvider>
      <ActiveGridProvider>
        <ExpenseResolutionByProjectInner />
      </ActiveGridProvider>
    </GridExportProvider>
  );
}
